<template>
  <div class="MyMapAtlasSummary" :type="type">
    <div class="d-flex flex-row flex-wrap">
      <div class="MyMapAtlasSummary_logo" :type="type">
        <img loading="lazy" :src="resolveMediaUrl(atlas.logo.contentUrl)" v-if="atlas.logo" />
      </div>
      <div class="MyMapAtlasSummary_desc">
        <v-tooltip :text="atlas.name" location="top" v-if="atlas.name.length > 19">
          <template v-slot:activator="{ props }">
            <div class="MyMapAtlasSummary_title" v-bind="props">
              {{ reduceText(atlas.name, 19) }}
            </div>
          </template>
        </v-tooltip>
        <div class="MyMapAtlasSummary_title" v-else>
          {{ atlas.name }}
        </div>
        <div class="MyMapAtlasSummary_details">
          {{ filteredLength }}
          {{
            type === AtlasGroup.PREDEFINED_MAP
              ? $t('myMap.atlases.map', { count: filteredLength })
              : $t('myMap.atlases.data', { count: filteredLength })
          }}
        </div>
      </div>
    </div>
    <v-btn size="small" icon="$arrowRight" class="text-dark-grey" @click="setActiveAtlas"></v-btn>
  </div>
</template>
<script setup lang="ts">
import { resolveMediaUrl } from '@/config/env'
import { AtlasGroup } from '@/models/enums/geo/AtlasGroup'
import type { Atlas } from '@/models/interfaces/Atlas'
import { reduceText } from '@/services/utils/UtilsService'
import { useMyMapStore } from '@/stores/myMapStore'
import { computed, inject, type Ref } from 'vue'

const props = defineProps<{
  atlas: Atlas
  type: AtlasGroup
}>()
const hideDetails: Ref<boolean> = inject('hideDetails') as Ref<boolean>
const mapStore = useMyMapStore()

function setActiveAtlas() {
  hideDetails.value = false
  if (props.type === AtlasGroup.PREDEFINED_MAP) {
    mapStore.activeAtlas.leftPanel.active = true
    mapStore.activeAtlas.leftPanel.atlasID = props.atlas['@id']
  } else {
    mapStore.activeAtlas.rightPanel.active = true
    mapStore.activeAtlas.rightPanel.atlasID = props.atlas['@id']
  }
}

const filteredLength = computed(() => {
  if (
    props.type === AtlasGroup.PREDEFINED_MAP ||
    !mapStore.atlasSearchText ||
    props.atlas.name.toLowerCase().includes(mapStore.atlasSearchText.toLowerCase())
  ) {
    return props.atlas.maps.length
  }
  return props.atlas.maps.filter((map) =>
    map.name.toLowerCase().includes(mapStore.atlasSearchText.toLowerCase()),
  ).length
})
</script>

<style lang="scss">
.MyMapAtlasSummary {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-radius: var(--dim-radius);
  padding: 0.75rem 1rem;
  min-height: var(--panel-row-min-height);
  border: 1px solid var(--neutral-border);
  background-color: var(--neutral-surface-subtle);
  transition: background-color 0.2s;
}
.MyMapAtlasSummary:hover {
  background-color: var(--neutral-hover);
}
.MyMapAtlasSummary[style*='display: none'] {
  display: none !important;
}
.MyMapAtlasSummary[type='Catalogue'] {
  border-color: var(--neutral-border);
}

.MyMapAtlasSummary[type='Observatoire'] {
  border-color: rgb(var(--v-theme-main-blue));
  box-shadow: none;
}

.MyMapAtlasSummary_logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  img {
    width: 2rem;
    height: 2rem;
    object-fit: cover;
    margin-right: 10px;
    border-radius: 50%;
  }
  &[type='Observatoire'] {
    img {
      width: 3rem;
      height: 3rem;
    }
  }
}

.MyMapAtlasSummary_desc {
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
}

.MyMapAtlasSummary_title {
  font-size: var(--panel-row-title-size);
  font-weight: var(--panel-row-title-weight);
  color: rgb(var(--v-theme-main-blue));
}

.MyMapAtlasSummary_details {
  font-size: var(--panel-label-size);
  color: var(--panel-label-color);
  margin-top: 0.125rem;
}
</style>
